import { FC, useState, useMemo } from 'react';
import { ClipboardDocumentIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import SelectInput from '@/components/functional/SelectInput/SelectInput';

type GenerationPrompt = {
  category_code: string;
  name: string;
  subject: 'English Language' | 'English Literature';
  paper: 'Paper 1' | 'Paper 2';
  marks: number;
  hasReadingInsert: boolean;
  description: string;
  prompt: string;
};

const OUTPUT_FORMAT = `Return ONLY a JSON object with no extra commentary, using this exact shape:
{
  "content": "<question text in markdown>",
  "marks": <number>,
  "reading_insert_content": "<extract text in markdown, or null if not applicable>",
  "mark_scheme_content": { <mark scheme object — see structure below> },
  "model_answer": "<model answer in markdown>",
  "hint_content": "<a concise student-friendly hint, 2–3 sentences>"
}`;

const MARK_SCHEME_RETRIEVAL = `mark_scheme_content structure for retrieval questions:
{
  "total_marks": <number>,
  "assessment_objectives": ["AO1"],
  "indicative_content": ["acceptable answer 1", "acceptable answer 2", "(Any other valid point.)"]
}
Note: NO "levels" array for pure retrieval questions.`;

const MARK_SCHEME_LEVELS = (levelCount: number, aos: string[]) => `mark_scheme_content structure (${levelCount}-level mark scheme):
{
  "total_marks": <number>,
  "assessment_objectives": ${JSON.stringify(aos)},
  "levels": [
    {
      "level": 1,
      "marks": "<range e.g. 1-2>",
      "descriptors": ["descriptor sentence 1", "descriptor sentence 2"]
    },
    ... repeat for all ${levelCount} levels ...
  ],
  "indicative_content": ["example point 1", "example point 2", "..."],
  "special_instructions": "<any specific marking guidance, or omit if none>"
}
IMPORTANT: "descriptors" must be an ARRAY of strings, not a single string.`;

const GENERATION_PROMPTS: GenerationPrompt[] = [
  // ─── English Language Paper 1 ───────────────────────────────────────────────
  {
    category_code: 'LANG-P1-Q1',
    name: 'Identify & Retrieve',
    subject: 'English Language',
    paper: 'Paper 1',
    marks: 4,
    hasReadingInsert: true,
    description: 'Short retrieval from a fiction text (4 marks, 1 mark per valid point)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 1 Question 1 practice question.

TASK: Generate a complete practice question with a short fiction extract.

QUESTION TYPE: Identify & Retrieve — students list four things from the extract that show a given aspect (e.g. character feelings, setting details, or events). Each correct, text-supported point scores 1 mark.

REQUIREMENTS:
- Write a fiction prose extract of 150–200 words. Setting should be vivid (e.g. an abandoned building, a storm, a crowded market). The extract must clearly contain at least six retrievable facts/details about one clear aspect (e.g. how a character feels, what the setting is like, what is happening).
- The question must direct students to specific lines (e.g. "Read lines 1–16.") and ask them to list FOUR things about one aspect.
- Example question stem: "Read lines 1–16 of the source. List four things about the [character/setting/event] from these lines."
- Marks: 4 (1 per point)
- Mark scheme: list exactly six acceptable points (students only need four).
- Model answer: a clean bulleted list of four strong points with brief textual evidence.
- Hint: remind students to use their own words and quote briefly; don't copy out long chunks.

${OUTPUT_FORMAT}

${MARK_SCHEME_RETRIEVAL}`,
  },
  {
    category_code: 'LANG-P1-Q2',
    name: 'Language Analysis',
    subject: 'English Language',
    paper: 'Paper 1',
    marks: 8,
    hasReadingInsert: true,
    description: 'Analyse language choices and their effects on the reader (8 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 1 Question 2 practice question.

TASK: Generate a complete practice question with a targeted fiction extract.

QUESTION TYPE: Language Analysis — students analyse how the writer uses language to describe something (e.g. a character, setting, atmosphere). Assessed on AO2: analysing language, form and structure with accurate use of subject terminology.

REQUIREMENTS:
- Write a rich fiction prose extract of 120–150 words focused on ONE clear subject (e.g. a character's fear, a threatening landscape, an exciting chase). Pack in varied language techniques: metaphor, simile, personification, sensory imagery, strong verbs, sentence length variation.
- The question must reference specific lines and name the subject of analysis.
- Question stem: "How does the writer use language here to describe [subject]? You could include the writer's choice of: words and phrases; language features and techniques; sentence forms." [8 marks]
- Marks: 8
- Mark scheme: 4-level descriptors (L1 simple awareness, L2 some understanding, L3 clear explanation, L4 perceptive/detailed analysis). Each level covers 2 marks (1–2, 3–4, 5–6, 7–8).
- Model answer: a structured paragraph-form response at Level 3–4 identifying 3 techniques with effect explained and brief quotation embedded.
- Hint: remind students to use the PETER method (Point, Evidence, Technique, Effect, Reader response) and zoom into individual word choices.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(3, ['AO2'])}`,
  },
  {
    category_code: 'LANG-P1-Q3',
    name: 'Structure Analysis',
    subject: 'English Language',
    paper: 'Paper 1',
    marks: 8,
    hasReadingInsert: true,
    description: 'Analyse how the whole text is structured to interest the reader (8 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 1 Question 3 practice question.

TASK: Generate a complete practice question with a full fiction extract for structural analysis.

QUESTION TYPE: Structure Analysis — students analyse how the writer has structured the WHOLE text to interest the reader. Assessed on AO2: structure, form, and language at the whole-text level. Key structural features: shifts in focus/time/setting, sentence/paragraph length, cyclical structure, openings, endings, narrative perspective.

REQUIREMENTS:
- Write a complete fiction prose extract of 300–400 words. It must have: a distinct opening hook, at least one clear shift in focus or time, variation in paragraph and sentence length, and a memorable final moment. Suitable genres: coming-of-age memory, tension/thriller, nature writing.
- The question should ask about the whole source.
- Question stem: "You now need to think about the whole of the Source. This text is from the beginning of a short story. How has the writer structured the text to interest you as a reader? You could write about: what the writer focuses your attention on at the beginning; how and why the writer changes this focus as the source develops; any other structural features that interest you." [8 marks]
- Marks: 8
- Mark scheme: 4-level descriptors. L1: simple comment on structure; L2: some understanding of structural choices; L3: clear explanation of structural effects; L4: perceptive analysis of whole-text structural choices with precise terminology.
- Model answer: structured response covering opening, development, and ending with terminology (e.g. "cyclical structure", "shift in focus", "short isolated paragraph").
- Hint: tell students to think about the text like a camera — where does the writer zoom in? zoom out? cut away? Why?

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(3, ['AO2'])}`,
  },
  {
    category_code: 'LANG-P1-Q4',
    name: 'Evaluation',
    subject: 'English Language',
    paper: 'Paper 1',
    marks: 20,
    hasReadingInsert: true,
    description: 'Evaluate how effectively the writer achieves their purpose (20 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 1 Question 4 practice question.

TASK: Generate a complete practice question with a longer fiction extract for evaluation.

QUESTION TYPE: Evaluation — students critically evaluate how effectively the writer achieves a stated purpose, assessed on AO4: evaluating writers' choices with evidence. Students must give a personal, evaluative response ("to what extent do you agree?").

REQUIREMENTS:
- Write a fiction prose extract of 400–500 words. It must contain a rich mix of language and structural techniques. Choose a focus that has two sides to evaluate (e.g. "the writer makes the reader feel sympathy for the narrator", "the writer creates an overwhelming sense of dread").
- The question must: direct students to a specific section (e.g. the second half); provide a student statement about the text; ask "to what extent do you agree?"; give 3 bullet-point prompts.
- Question stem: "Focus this part of your answer on the second half of the Source, from line [X] to the end. A student, having read this section of text, said: '[Student statement about the effect of the text].' To what extent do you agree? In your response, you could: evaluate how the writer [does X]; support your comments with references to the text; consider the statement in different ways before reaching your own conclusion." [20 marks]
- Marks: 20
- Mark scheme: 5-level descriptors (L1 simple, L2 some, L3 clear, L4 perceptive, L5 sophisticated). AO4: evaluate critically and with discrimination. Marks per level: 1–4, 5–8, 9–12, 13–16, 17–20.
- Model answer: a 4–5 paragraph evaluative response at Level 4, using "I agree to a large extent because…" structure, evaluating both what works and what is less convincing.
- Hint: remind students to use evaluative vocabulary ("highly effective", "particularly successful", "to a large extent") and make a clear overall judgement.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO4'])}`,
  },
  {
    category_code: 'LANG-P1-Q5',
    name: 'Creative Writing',
    subject: 'English Language',
    paper: 'Paper 1',
    marks: 40,
    hasReadingInsert: false,
    description: 'Descriptive or narrative imaginative writing task (40 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 1 Question 5 practice question.

TASK: Generate a complete creative writing practice question (no extract needed).

QUESTION TYPE: Creative Writing — students produce an extended piece of imaginative writing (either descriptive or narrative). Assessed on AO5 (communication, imaginative, engaging — 24 marks) and AO6 (technical accuracy — 16 marks).

REQUIREMENTS:
- Create TWO distinct task options (students choose one):
  OPTION A — Descriptive: based on a described scene or image (e.g. "Describe a busy railway station at night."). Should inspire sensory, atmospheric writing.
  OPTION B — Narrative: based on a title, opening line, or theme (e.g. "Write a story about a journey that changes everything."). Should invite plot, character, and tension.
- Each option should be clearly labelled and phrased as an exam instruction.
- Marks: 40 total (AO5: 24 marks for content and organisation; AO6: 16 marks for technical accuracy — SPaG, vocabulary, punctuation).
- Mark scheme: 5-level descriptors for AO5 (communication + organisation) and 4-level descriptors for AO6 (technical accuracy). Provide both separately.
- Model answer: a strong model creative piece (~400 words) for ONE of the two options, demonstrating varied techniques, sentence variety, and structure.
- Hint: advise students to spend 3–4 minutes planning structure before writing; remind them that quality beats quantity; encourage "show don't tell".

reading_insert_content should be null.

${OUTPUT_FORMAT}`,
  },

  // ─── English Language Paper 2 ───────────────────────────────────────────────
  {
    category_code: 'LANG-P2-Q1',
    name: 'Identify & Retrieve (Text 1)',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 1,
    hasReadingInsert: true,
    description: 'Single-point retrieval from non-fiction Text 1 (1 mark)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 1 practice question.

TASK: Generate a complete practice question with a short non-fiction extract (Text 1).

QUESTION TYPE: Identify & Retrieve — one straightforward fact retrieved from a non-fiction source. Worth 1 mark. No analysis needed.

REQUIREMENTS:
- Write a non-fiction prose extract of 120–160 words. Suitable types: newspaper article, travel writing, autobiography, opinion piece, report. The extract must contain one clear, unambiguous fact that students can identify.
- The question must direct students to a specific line range and ask for a single piece of information.
- Question stem: "Read lines 1–[X]. What [specific thing] does the writer mention?" [1 mark] OR "According to lines 1–[X], what is…?" [1 mark]
- Marks: 1
- Mark scheme: list 2–3 acceptable answers (paraphrase or direct quote both acceptable).
- Model answer: one clear sentence with the correct fact.
- Hint: tell students the answer is always directly stated — no inference needed; scan for the key word from the question.

${OUTPUT_FORMAT}

${MARK_SCHEME_RETRIEVAL}`,
  },
  {
    category_code: 'LANG-P2-Q2',
    name: 'Identify & Retrieve (Text 1)',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 2,
    hasReadingInsert: true,
    description: 'Two-point retrieval from non-fiction Text 1 (2 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 2 practice question.

TASK: Generate a complete practice question with a non-fiction extract (Text 1) — can reuse the Q1 extract or create a new one.

QUESTION TYPE: Identify & Retrieve — students list two things from the source. 1 mark each.

REQUIREMENTS:
- Write or reuse a non-fiction extract of 150–200 words. The extract must clearly contain at least four retrievable points about one aspect (e.g. the writer's opinions, reasons given for something, conditions described).
- The question directs students to a line range and asks them to list TWO things.
- Question stem: "Read lines [X]–[Y]. List two [things/reasons/ways] that the writer mentions." [2 marks]
- Marks: 2
- Mark scheme: list four acceptable points (students only need two).
- Model answer: two clear bullet points with brief supporting reference.
- Hint: same as Q1 — answers are directly stated, no inference needed; use your own words.

${OUTPUT_FORMAT}

${MARK_SCHEME_RETRIEVAL}`,
  },
  {
    category_code: 'LANG-P2-Q3',
    name: 'Language Analysis',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 6,
    hasReadingInsert: true,
    description: 'Analyse language in non-fiction Text 1 (6 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 3 practice question.

TASK: Generate a complete practice question with a targeted non-fiction extract (Text 1) for language analysis.

QUESTION TYPE: Language Analysis of non-fiction — students analyse how the writer uses language for effect in a targeted section. Assessed on AO2.

REQUIREMENTS:
- Write a non-fiction extract of 150–200 words with a clear purpose and audience. Examples: a passionate opinion piece, a vivid travel article, a campaigning letter. Include persuasive/rhetorical techniques: direct address, rhetorical questions, emotive language, lists of three, contrast, hyperbole, expert/statistical evidence.
- The question must reference specific lines and name what to analyse.
- Question stem: "How does the writer use language to [argue/inform/persuade/describe] in lines [X]–[Y]? You could include the writer's choice of: words and phrases; language features and techniques; sentence forms." [15 marks]
- Marks: 15
- Mark scheme: 5-level descriptors. L1: 1–3 limited comment; L2: 4–6 comment with some explanation; L3: 7–9 explanation of language and structure; L4: 10–12 exploration of both; L5: 13–15 analysis of both. Each level must have "descriptors" as an array of strings. AO2.
- Model answer: a paragraph-form response at Level 4–5 with 3 techniques identified, quotations embedded, and effects explained across language and structure.
- Hint: remind students that non-fiction uses persuasive techniques just like fiction uses descriptive ones; PETER structure works well here too.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO2'])}`,
  },
  {
    category_code: 'LANG-P2-Q4',
    name: 'Identify & Retrieve (Text 2)',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 1,
    hasReadingInsert: true,
    description: 'Single-point retrieval from non-fiction Text 2 (1 mark)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 4 practice question.

TASK: Generate a complete practice question with a second non-fiction extract (Text 2) from a different era or perspective than Text 1.

QUESTION TYPE: Identify & Retrieve from Text 2 — one straightforward fact. 1 mark.

REQUIREMENTS:
- Write a non-fiction extract of 120–160 words. This should be a contrasting source to a potential Text 1 (e.g. if Text 1 is a modern travel article, Text 2 could be a Victorian travel journal, or if Text 1 is pro-technology, Text 2 is sceptical). The extract must contain one unambiguous fact.
- Question must reference Text 2 specifically and a line range.
- Question stem: "Read lines 1–[X] of Source B. According to the writer, what…?" OR "What does the writer tell us about…?" [1 mark]
- Marks: 1
- Mark scheme: 2–3 acceptable phrasings of the answer.
- Model answer: one clear sentence.
- Hint: same approach as Q1 — find the key word from the question in the text and pull out the fact directly beside it.

${OUTPUT_FORMAT}

${MARK_SCHEME_RETRIEVAL}`,
  },
  {
    category_code: 'LANG-P2-Q5',
    name: 'Identify & Retrieve (Text 2)',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 2,
    hasReadingInsert: true,
    description: 'Two-point retrieval from non-fiction Text 2 (2 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 5 practice question.

TASK: Generate a complete practice question using Text 2 from the Paper 2 non-fiction pair.

QUESTION TYPE: Identify & Retrieve from Text 2 — two facts/points. 1 mark each.

REQUIREMENTS:
- Use or create a non-fiction extract of 150–200 words as Text 2 (contrasting perspective to Text 1). Must contain at least four retrievable points on one aspect.
- The question directs students to a line range of Text 2 and asks for TWO things.
- Question stem: "Read lines [X]–[Y] of Source B. Give two [reasons/ways/things] the writer mentions about [topic]." [2 marks]
- Marks: 2
- Mark scheme: four acceptable points (students need two).
- Model answer: two bullet points with short supporting quote or paraphrase.
- Hint: scan for the key concept from the question; answers are always explicitly stated, never implied.

${OUTPUT_FORMAT}

${MARK_SCHEME_RETRIEVAL}`,
  },
  {
    category_code: 'LANG-P2-Q6',
    name: 'Evaluation',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 6,
    hasReadingInsert: true,
    description: 'Evaluate how effectively the writer achieves purpose in Text 2 (6 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 6 practice question.

TASK: Generate a complete practice question with a longer section of Text 2 for evaluation.

QUESTION TYPE: Evaluation of non-fiction — students evaluate how effectively the writer achieves their purpose/convinces the reader. Assessed on AO4.

REQUIREMENTS:
- Write a non-fiction extract of 250–350 words with a clear argument or viewpoint (e.g. a persuasive article about social media, an editorial about urban nature, a polemic about education). The writer's methods must be evaluable — some clearly effective, perhaps some less so.
- The question provides a student statement and asks "to what extent do you agree?".
- Question stem: "'[Student statement about how effective/convincing the writer is].' To what extent do you agree with this view of Source B? In your response you should: evaluate how the writer has tried to [achieve purpose]; support your evaluation with references from the text; consider whether the student's view is valid." [15 marks]
- Marks: 15
- Mark scheme: 5-level descriptors (L1: 1–3 description/limited assertions; L2: 4–6 comment/straightforward opinions; L3: 7–9 explanation/informed judgement; L4: 10–12 analysis/well-informed critical judgement; L5: 13–15 evaluation/sustained critical overview). AO4.
- Model answer: 3–4 paragraphs at Level 4 evaluating effectiveness with well-chosen evidence and a clear overall judgement.
- Hint: students must give a verdict — don't just analyse. Use "to a large/limited extent…" language and weigh up the techniques.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO4'])}`,
  },
  {
    category_code: 'LANG-P2-Q7A',
    name: 'Synthesis – Similarities',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 6,
    hasReadingInsert: true,
    description: 'Synthesise similarities between two non-fiction texts (6 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 7A practice question.

TASK: Generate a complete practice question requiring synthesis across TWO non-fiction texts.

QUESTION TYPE: Synthesis of similarities — students identify and synthesise similar ideas/information from both texts. Assessed on AO1: identify and interpret information and ideas; select and synthesise evidence.

REQUIREMENTS:
- Create TWO related non-fiction extracts (Text 1 and Text 2), each 150–200 words, on the SAME broad theme but from different perspectives/eras (e.g. both about city life — one modern blog, one Victorian essay). They must share at least FOUR points of similarity in ideas/information (not necessarily language).
- The question asks students to find similarities using BOTH texts.
- Question stem: "The two sources are both about [shared theme]. Use details from BOTH sources to write a summary of the similarities between [specific aspect of the theme]." [6 marks]
- Marks: 6
- Mark scheme: 3-level descriptors. L1: simple comments from one or both texts; L2: some synthesis with references; L3: clear synthesis with well-chosen references from both texts combined into flowing points.
- Model answer: 3 synthesised points, each drawing on BOTH texts, written as flowing prose.
- Hint: students must reference BOTH texts in each point — don't just list one then the other. Use "Both writers…", "Similarly…", "In the same way…" connectives.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(3, ['AO1'])}`,
  },
  {
    category_code: 'LANG-P2-Q7B',
    name: 'Synthesis – Differences',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 6,
    hasReadingInsert: true,
    description: 'Compare different attitudes and perspectives across two texts (6 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Question 7B practice question.

TASK: Generate a complete practice question requiring comparison of DIFFERENCES across two non-fiction texts.

QUESTION TYPE: Synthesis of differences — students compare how the two writers present contrasting ideas, attitudes, or perspectives. Assessed on AO1.

REQUIREMENTS:
- Create TWO non-fiction extracts (Text 1 and Text 2), each 150–200 words, on the same theme but with CONTRASTING viewpoints (e.g. one optimistic about technology, one fearful; one celebrating city life, one preferring countryside). Each text must clearly show the writer's attitude, not just give neutral information.
- The question asks students to compare the differences in attitude/perspective.
- Question stem: "Both sources are about [shared theme]. Compare how the two writers convey their different attitudes and perspectives about [specific aspect]. In your answer you could: compare their different views; compare the methods they use to convey their attitudes; support your response with references from both texts." [14 marks]
- Marks: 14
- Mark scheme: 5-level descriptors. L1: 1–2 no comparison/description only; L2: 3–5 obvious comparisons/comment; L3: 6–8 range of comparisons/explanation; L4: 9–11 wide range of comparisons/exploration; L5: 12–14 varied and comprehensive comparisons/analysis. AO3.
- Model answer: 3–4 comparative paragraphs using "Whereas…", "In contrast…", "Unlike Source A, Source B…" structure.
- Hint: always compare both what they think (attitude) AND how they show it (method). Don't just list — connect the two sources in each point.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO3'])}`,
  },
  {
    category_code: 'LANG-P2-B',
    name: 'Transactional Writing',
    subject: 'English Language',
    paper: 'Paper 2',
    marks: 40,
    hasReadingInsert: false,
    description: 'Write for a specific purpose and audience — Q8 or Q9 choice (40 marks)',
    prompt: `You are creating a GCSE English Language (Edexcel) Paper 2 Section B practice question.

TASK: Generate a complete transactional writing practice question (no extract needed).

QUESTION TYPE: Transactional Writing — students write a non-fiction piece for a specific form, purpose, and audience. Assessed on AO5 (24 marks) and AO6 (16 marks). Students choose ONE from two tasks (Q8 or Q9).

REQUIREMENTS:
- Create TWO distinct tasks (Q8 and Q9), each specifying a different form and purpose. Both should link thematically to a topic that could relate to the Section A reading theme (e.g. environment, education, technology, sport). Examples:
  Q8: Write a letter to your headteacher arguing for/against [a school policy].
  Q9: Write an article for a magazine aimed at teenagers exploring [a social issue].
- Each task must clearly state: FORM (letter, article, speech, report, review), PURPOSE (argue, persuade, advise, explain, inform, describe), AUDIENCE (who they are writing for).
- Marks: 40 total (AO5: 24 communication + organisation; AO6: 16 technical accuracy).
- Mark scheme: 5-level descriptors for AO5 and 4-level descriptors for AO6 (same as Paper 1 Q5). Provide both.
- Model answer: a strong ~350-word model response for ONE of the two tasks showing clear form conventions, persuasive techniques, and structural control.
- Hint: remind students to use the correct form conventions (letter greeting, article headline, speech opener) and vary rhetorical techniques (rhetorical questions, statistics, direct address, anecdote, rule of three).

reading_insert_content should be null.

${OUTPUT_FORMAT}`,
  },

  // ─── English Literature Paper 1 ─────────────────────────────────────────────
  {
    category_code: 'LIT-P1-Q1A',
    name: 'Shakespeare – Extract',
    subject: 'English Literature',
    paper: 'Paper 1',
    marks: 20,
    hasReadingInsert: true,
    description: 'Analyse an extract from a Shakespeare play (20 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 1 Question 1A practice question.

TASK: Generate a complete practice question with a Shakespeare extract for analysis.

QUESTION TYPE: Shakespeare Extract — students start with the given extract and then consider the whole play, analysing how Shakespeare presents a theme or character. Assessed on AO1 (response to text) and AO2 (language, form, structure).

REQUIREMENTS:
- Choose ONE of the Edexcel GCSE Shakespeare texts: Macbeth, Romeo and Juliet, The Merchant of Venice, Much Ado About Nothing, or Othello. Pick a specific moment in the play.
- Write a Shakespeare extract of 30–50 lines that clearly showcases a key theme or character trait (e.g. ambition in Macbeth, love in Romeo and Juliet, jealousy in Othello). You may slightly adapt/simplify language if needed for practice purposes, but keep it authentic in style.
- The question must name the theme/character and ask students to start with the extract.
- Question stem: "In this extract, how does Shakespeare present [theme/character]? Starting with this extract, write about how Shakespeare presents [theme/character] in [play title]. [20 marks] (AO1 and AO2)"
- Marks: 20
- Mark scheme: 5-level descriptors (L1: 1–4, L2: 5–8, L3: 9–12, L4: 13–16, L5: 17–20). AO1: informed personal response; AO2: analysis of language/form/structure with subject terminology.
- Model answer: a structured response (~450 words) covering the extract in detail and making ONE reference to the wider play.
- Hint: students should spend more time on the extract than the wider play (roughly 70/30 split); identify Shakespeare's specific word choices and dramatic techniques.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2'])}`,
  },
  {
    category_code: 'LIT-P1-Q1B',
    name: 'Shakespeare – Whole Text',
    subject: 'English Literature',
    paper: 'Paper 1',
    marks: 20,
    hasReadingInsert: false,
    description: 'Essay on the whole Shakespeare play (20 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 1 Question 1B practice question.

TASK: Generate a complete whole-text Shakespeare essay question (no extract — students draw on the whole play from memory).

QUESTION TYPE: Shakespeare Whole Text — students write an essay about the whole play responding to a theme or character, without a given extract. Assessed on AO1 and AO2.

REQUIREMENTS:
- Choose ONE of the Edexcel GCSE Shakespeare texts: Macbeth, Romeo and Juliet, The Merchant of Venice, Much Ado About Nothing, or Othello.
- Create a question focusing on a major theme or character that allows students to draw on multiple scenes/moments from the play.
- Question format options:
  Option A: "'[Critical statement about the play/character/theme].' How far do you agree with this view? Write about how Shakespeare presents [X] in [play]."
  Option B: "How does Shakespeare present [theme] in [play]? Write about: how Shakespeare explores [aspect 1]; how Shakespeare presents [aspect 2]."
- Marks: 20
- Mark scheme: 5-level descriptors (same as Q1A). L5 students make perceptive, detailed analytical points with well-chosen textual references and sophisticated terminology.
- Model answer: a 4-paragraph essay (~450 words) with an introduction, two developed analytical points with specific references, and a conclusion.
- Hint: students should reference at least 3 specific moments from across the play; use short embedded quotations rather than long ones; always connect back to the question.

reading_insert_content should be null.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2'])}`,
  },
  {
    category_code: 'LIT-P1-Q2',
    name: 'Post-1914 Literature',
    subject: 'English Literature',
    paper: 'Paper 1',
    marks: 40,
    hasReadingInsert: false,
    description: 'Essay on a post-1914 British play or novel (40 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 1 Question 2 practice question.

TASK: Generate a complete essay question on a post-1914 British text (no extract — whole text response).

QUESTION TYPE: Post-1914 Literature Essay — students write an extended essay on a post-1914 British play or novel studied. Assessed on AO1 (40%), AO2 (40%), and AO3 contexts (20%). This is the longest literature question (40 marks).

REQUIREMENTS:
- Choose ONE of the common Edexcel GCSE post-1914 texts: An Inspector Calls (Priestley), Lord of the Flies (Golding), Animal Farm (Orwell), The Woman in Black (Hill), or DNA (Kelly).
- Create a question that requires students to engage with theme, character, and context.
- Question format: "How does [author] present [theme/character] in [text title]? Write about: how [author] develops [aspect 1] in the text; how [author] presents [aspect 2] in the text; the methods [author] uses to present [theme/character]." [40 marks] (AO1, AO2, AO3)
- Marks: 40
- Mark scheme: 5-level descriptors covering AO1 (personal, informed response), AO2 (analysis of methods), AO3 (relevant context). Mark ranges: L1: 1–8, L2: 9–16, L3: 17–24, L4: 25–32, L5: 33–40.
- Model answer: a 5-paragraph essay (~600 words) with intro, three developed analytical body paragraphs (each with context woven in), and a conclusion.
- Hint: context is worth 20% — students must weave in historical, social, or biographical context naturally within each point, not as a separate paragraph.

reading_insert_content should be null.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2', 'AO3'])}`,
  },

  // ─── English Literature Paper 2 ─────────────────────────────────────────────
  {
    category_code: 'LIT-P2-Q1A',
    name: '19th-Century Novel – Extract',
    subject: 'English Literature',
    paper: 'Paper 2',
    marks: 20,
    hasReadingInsert: true,
    description: 'Analyse an extract from a 19th-century novel (20 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 2 Question 1A practice question.

TASK: Generate a complete practice question with a 19th-century novel extract.

QUESTION TYPE: 19th-Century Novel Extract — students start with the extract and then consider the whole novel, analysing theme or character. Assessed on AO1, AO2, and AO3 (context).

REQUIREMENTS:
- Choose ONE of the common Edexcel GCSE 19th-century novels: Great Expectations (Dickens), Jekyll and Hyde (Stevenson), A Christmas Carol (Dickens), Jane Eyre (Brontë), or The Sign of Four (Conan Doyle).
- Write an extract of 30–50 lines from a key dramatic moment in the novel — one that strongly showcases a theme or character trait. Adapt slightly for clarity if needed, but maintain the Victorian prose style and vocabulary.
- The question must name the theme/character and ask students to start with the extract.
- Question stem: "In this extract, how does [author's surname] present [theme/character]? Starting with this extract, write about how [author] presents [theme/character] in [novel title]. [20 marks] (AO1, AO2, AO3)"
- Marks: 20
- Mark scheme: 5-level descriptors. L5 students demonstrate sophisticated analysis of language, structure and form with perceptive context links. Mark ranges: L1: 1–4, L2: 5–8, L3: 9–12, L4: 13–16, L5: 17–20.
- Model answer: a structured response (~450 words) analysing the extract in detail with specific language analysis and one reference to the wider novel plus a brief context point.
- Hint: students must include at least one reference to 19th-century context (e.g. Victorian society, class, gender roles, scientific anxiety) — it is worth marks.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2', 'AO3'])}`,
  },
  {
    category_code: 'LIT-P2-Q1B',
    name: '19th-Century Novel – Whole Text',
    subject: 'English Literature',
    paper: 'Paper 2',
    marks: 20,
    hasReadingInsert: false,
    description: 'Essay on the whole 19th-century novel (20 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 2 Question 1B practice question.

TASK: Generate a complete whole-text essay question for a 19th-century novel.

QUESTION TYPE: 19th-Century Novel Whole Text — students write from memory about the whole novel, focusing on theme, character, or writer's craft. Assessed on AO1, AO2, and AO3.

REQUIREMENTS:
- Choose ONE of the Edexcel GCSE 19th-century novels: Great Expectations, Jekyll and Hyde, A Christmas Carol, Jane Eyre, or The Sign of Four.
- Create a question asking about a significant theme or character across the whole novel.
- Question format: "'[Critical statement about the novel/character/theme].' How far do you agree? Write about how [author] presents [theme/character] in [novel], referring to context where relevant." [20 marks]
- Marks: 20
- Mark scheme: 5-level descriptors covering AO1, AO2, AO3. Context must be relevant and integrated, not bolted on.
- Model answer: a 4-paragraph essay (~450 words) covering 3 distinct moments from across the novel, with language analysis and contextual references woven naturally into each paragraph.
- Hint: students should refer to at least 3 different moments in the novel; never write a separate "context paragraph" — weave it into each point.

reading_insert_content should be null.

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2', 'AO3'])}`,
  },
  {
    category_code: 'LIT-P2-Q2',
    name: 'Poetry – Named Poem',
    subject: 'English Literature',
    paper: 'Paper 2',
    marks: 20,
    hasReadingInsert: true,
    description: 'Analyse a named poem from the anthology (20 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 2 Question 2 practice question.

TASK: Generate a complete poetry analysis question for a named poem from the Edexcel anthology.

QUESTION TYPE: Poetry Named Poem — students analyse a specific named poem printed on the paper. No comparison needed. Assessed on AO1 and AO2.

REQUIREMENTS:
- Choose ONE poem from the Edexcel GCSE Poetry Anthology (either the "Conflict" cluster or the "Time and Place" cluster). Examples: "Exposure" (Owen), "Remains" (Armitage), "Poppies" (Weir), "Tissue" (Dharker), "The Emigree" (Rumens), "Kamikaze" (Garland).
- Include the full text of the chosen poem as the reading_insert_content.
- Create a question asking how the poet presents a specific theme/emotion found in the poem.
- Question stem: "In '[Poem Title]', how does the poet present [theme/emotion/experience]? Support your answer with reference to the poem. [20 marks] (AO1 and AO2)"
- Marks: 20
- Mark scheme: 5-level descriptors. L5: sophisticated analysis of poetic methods (imagery, structure, form, tone, voice, sound devices) with perceptive interpretation. Mark ranges: L1: 1–4, L2: 5–8, L3: 9–12, L4: 13–16, L5: 17–20.
- Model answer: a structured response (~450 words) covering: overall theme/effect, 2–3 specific language devices with quotations, a structural/form point, and a brief conclusion.
- Hint: students should analyse specific words, not just techniques — "the word 'obliterate' suggests complete destruction, conveying the poet's sense that war erases all humanity."

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2'])}`,
  },
  {
    category_code: 'LIT-P2-Q3',
    name: 'Poetry – Comparison',
    subject: 'English Literature',
    paper: 'Paper 2',
    marks: 20,
    hasReadingInsert: true,
    description: 'Compare two poems from the anthology (20 marks)',
    prompt: `You are creating a GCSE English Literature (Edexcel) Paper 2 Question 3 practice question.

TASK: Generate a complete poetry comparison question using two poems from the Edexcel anthology.

QUESTION TYPE: Poetry Comparison — students compare how TWO poets present a shared theme. One poem is named; students choose the second. Assessed on AO1, AO2, and AO3.

REQUIREMENTS:
- Choose ONE named poem from the Edexcel GCSE Poetry Anthology as the "given" poem (e.g. "War Photographer" by Duffy, "Bayonet Charge" by Hughes).
- Include the full text of the named poem as the reading_insert_content.
- The second poem should be chosen by the student from the same cluster — do NOT name it in the question.
- Create a question asking students to compare how BOTH poets present a shared theme/idea.
- Question stem: "Compare how [theme] is presented in '[Named Poem]' by [poet] and ONE other poem from your anthology. [20 marks] (AO1, AO2, AO3)"
- Marks: 20
- Mark scheme: 5-level descriptors with marks for comparison (AO1), analysis of poetic methods in both poems (AO2), and contextual relevance (AO3). Mark ranges: L1: 1–4, L2: 5–8, L3: 9–12, L4: 13–16, L5: 17–20.
- Model answer: a structured comparison (~500 words) covering: introduction naming both poems, 3 comparative paragraphs each discussing both poems with quotations and analysis, and a brief conclusion with overall comparison.
- Hint: students should compare throughout — every paragraph must discuss both poems. Use "Both poets…", "While [Poet A] uses X, [Poet B] instead…", "In contrast…".

${OUTPUT_FORMAT}

${MARK_SCHEME_LEVELS(5, ['AO1', 'AO2', 'AO3'])}`,
  },
];

const SUBJECT_OPTIONS = [
  { value: '', label: 'All Subjects' },
  { value: 'English Language', label: 'English Language' },
  { value: 'English Literature', label: 'English Literature' },
];

const PAPER_OPTIONS = [
  { value: '', label: 'All Papers' },
  { value: 'Paper 1', label: 'Paper 1' },
  { value: 'Paper 2', label: 'Paper 2' },
];

const SUBJECT_BADGE: Record<string, string> = {
  'English Language': 'bg-blue-100 text-blue-800',
  'English Literature': 'bg-purple-100 text-purple-800',
};

const PromptCard: FC<{ config: GenerationPrompt }> = ({ config }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(config.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <code className="font-mono text-sm font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded shrink-0">
            {config.category_code}
          </code>
          <span className="font-medium text-gray-900 truncate">{config.name}</span>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${SUBJECT_BADGE[config.subject]}`}
          >
            {config.marks} marks
          </span>
          {config.hasReadingInsert && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 shrink-0">
              needs extract
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className="flex items-center gap-1.5 text-sm font-medium text-[#C87941] hover:text-[#A66835] transition-colors"
          >
            {copied ? (
              <>
                <CheckIcon className="w-4 h-4" />
                Copied
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="w-4 h-4" />
                Copy prompt
              </>
            )}
          </button>
          {expanded ? (
            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Description */}
      <div className="px-6 pb-3 text-sm text-gray-500 border-t border-gray-100 pt-3">
        {config.description}
      </div>

      {/* Expanded prompt */}
      {expanded && (
        <div className="px-6 pb-6">
          <div className="relative">
            <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed overflow-auto max-h-96">
              {config.prompt}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionGenerationPromptsPage: FC = () => {
  const [subjectFilter, setSubjectFilter] = useState('');
  const [paperFilter, setPaperFilter] = useState('');

  const filtered = useMemo(() => {
    return GENERATION_PROMPTS.filter((p) => {
      if (subjectFilter && p.subject !== subjectFilter) return false;
      if (paperFilter && p.paper !== paperFilter) return false;
      return true;
    });
  }, [subjectFilter, paperFilter]);

  const groups = useMemo(() => {
    const map = new Map<string, GenerationPrompt[]>();
    for (const p of filtered) {
      const key = `${p.subject} – ${p.paper}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return map;
  }, [filtered]);

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white rounded-3xl flex justify-between items-center px-8 py-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Question Generation Prompts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Copy a prompt and paste it into Claude or ChatGPT to generate a ready-to-import practice question.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <div className="flex gap-4">
          <SelectInput
            label="Subject"
            value={subjectFilter}
            onChange={setSubjectFilter}
            options={SUBJECT_OPTIONS}
            wrapperClassName="w-56"
          />
          <SelectInput
            label="Paper"
            value={paperFilter}
            onChange={setPaperFilter}
            options={PAPER_OPTIONS}
            wrapperClassName="w-48"
          />
        </div>
      </div>

      {/* Prompt groups */}
      {[...groups.entries()].map(([groupKey, prompts]) => (
        <div key={groupKey} className="bg-white rounded-3xl mt-6 px-8 py-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{groupKey}</h2>
          <div className="space-y-3">
            {prompts.map((config) => (
              <PromptCard key={config.category_code} config={config} />
            ))}
          </div>
        </div>
      ))}

      {/* Output format reference */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Expected JSON Output Fields</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {[
            { field: 'content', desc: 'The question text (markdown)' },
            { field: 'marks', desc: 'Total marks as a number' },
            { field: 'reading_insert_content', desc: 'The extract/source text (markdown), or null' },
            { field: 'mark_scheme_content', desc: 'Mark scheme as a JSON object' },
            { field: 'model_answer', desc: 'A model answer (markdown)' },
            { field: 'hint_content', desc: 'A concise student-friendly hint' },
          ].map(({ field, desc }) => (
            <div key={field} className="flex items-start gap-2">
              <code className="bg-gray-100 px-2 py-0.5 rounded text-gray-800 shrink-0">{field}</code>
              <span className="text-gray-600">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuestionGenerationPromptsPage;
